// src/hooks/useTelephonyAvailability.ts
// Hook to check if telephony (Twilio) is available for the current workspace

import { useMemo } from 'react';
import { useGetIntegrationStatusQuery } from '../api/integrationsApi';
import { useListPhoneNumbersQuery } from '../api/telephonyApi';
import { useActiveWorkspace } from './useActiveWorkspace';
import { useHasCapability } from '../services/centralContext';
import { FEATURE_CAPABILITIES } from '../config/featureCapabilities';

export type TelephonyUnavailableReason =
  | 'no_workspace'
  | 'no_integration'
  | 'integration_error'
  | 'no_phone_numbers'
  | 'no_permission';

interface UseTelephonyAvailabilityResult {
  /** Whether telephony features are available (any of phone/sms read) */
  isTelephonyAvailable: boolean;
  /** Whether the Phone surface is available (integration ok + numbers + phone.read) */
  isPhoneAvailable: boolean;
  /** Whether the SMS surface is available (integration ok + numbers + sms.read) */
  isSmsAvailable: boolean;
  /** Reason telephony is unavailable (if applicable) */
  reason?: TelephonyUnavailableReason;
  /** Central capability: phone.call */
  canMakeCalls: boolean;
  /** Central capability: phone.read */
  canViewCalls: boolean;
  /** Central capability: sms.send */
  canSendSms: boolean;
  /** Central capability: sms.read */
  canViewSms: boolean;
  /** Convenience: any of phone.read / sms.read */
  canViewTelephony: boolean;
  /** Whether the data is still loading */
  isLoading: boolean;
  /** Active workspace ID */
  workspaceId?: string;
  /** List of available phone numbers */
  phoneNumbers: { id: string; phoneNumber: string; label: string | null }[];
}

export function useTelephonyAvailability(): UseTelephonyAvailabilityResult {
  const activeWorkspace = useActiveWorkspace();
  const workspaceId = activeWorkspace?.workspace.id;
  const loadingWorkspaces = false; // useActiveWorkspace handles loading internally

  const { data: integrationStatus, isLoading: loadingIntegrations } = useGetIntegrationStatusQuery(
    undefined,
    { skip: !workspaceId }
  );

  const { data: phoneNumbers, isLoading: loadingNumbers } = useListPhoneNumbersQuery(undefined, {
    skip: !workspaceId,
  });

  // Central capability checks — these come from
  // workspace.capabilities["crm"].{phone,sms}.{read,call,send}
  const canViewCalls = useHasCapability(
    FEATURE_CAPABILITIES.viewCalls.domain,
    FEATURE_CAPABILITIES.viewCalls.action
  );
  const canMakeCalls = useHasCapability(
    FEATURE_CAPABILITIES.makeCall.domain,
    FEATURE_CAPABILITIES.makeCall.action
  );
  const canViewSms = useHasCapability(
    FEATURE_CAPABILITIES.viewSms.domain,
    FEATURE_CAPABILITIES.viewSms.action
  );
  const canSendSms = useHasCapability(
    FEATURE_CAPABILITIES.sendSms.domain,
    FEATURE_CAPABILITIES.sendSms.action
  );
  const canViewTelephony = canViewCalls || canViewSms || canMakeCalls || canSendSms;

  const result = useMemo<UseTelephonyAvailabilityResult>(() => {
    const isLoading = loadingWorkspaces || loadingIntegrations || loadingNumbers;

    const baseFlags = {
      canMakeCalls,
      canViewCalls,
      canSendSms,
      canViewSms,
      canViewTelephony,
    };

    // Check workspace
    if (!workspaceId) {
      return {
        isTelephonyAvailable: false,
        isPhoneAvailable: false,
        isSmsAvailable: false,
        reason: 'no_workspace',
        ...baseFlags,
        canMakeCalls: false,
        canViewCalls: false,
        canSendSms: false,
        canViewSms: false,
        canViewTelephony: false,
        isLoading,
        phoneNumbers: [],
      };
    }

    // Central capability page-level gate removed. The Phone page is visible
    // whenever the CRM in-app role allows the sidebar item; the server still
    // enforces phone.read on any API hit. Individual capability flags remain
    // in the returned object for finer-grained button gating.

    // Check Twilio integration status
    const twilioStatus = integrationStatus?.statuses.find((s) => s.provider === 'twilio');
    if (!twilioStatus || twilioStatus.status === 'not_connected') {
      return {
        isTelephonyAvailable: false,
        isPhoneAvailable: false,
        isSmsAvailable: false,
        reason: 'no_integration',
        ...baseFlags,
        isLoading,
        workspaceId,
        phoneNumbers: [],
      };
    }

    // Hard error from the integration (auth failure, expired credentials, etc.)
    // Server marks `status='error'` only on real connectivity failures.
    if (twilioStatus.status === 'error') {
      return {
        isTelephonyAvailable: false,
        isPhoneAvailable: false,
        isSmsAvailable: false,
        reason: 'integration_error',
        ...baseFlags,
        isLoading,
        workspaceId,
        phoneNumbers: [],
      };
    }

    // `needs_attention` for Twilio means the credentials are valid but
    // some setup is incomplete — most commonly "no phone numbers synced".
    // Don't treat that as a hard error; fall through to the phone-number
    // check below, which gives the user the correct "No Phone Numbers"
    // empty state with a Settings deep-link to run the sync.

    // Check if we have phone numbers
    const availableNumbers = phoneNumbers?.filter((n) => n.status === 'active') || [];
    if (availableNumbers.length === 0) {
      return {
        isTelephonyAvailable: false,
        isPhoneAvailable: false,
        isSmsAvailable: false,
        reason: 'no_phone_numbers',
        ...baseFlags,
        isLoading,
        workspaceId,
        phoneNumbers: [],
      };
    }

    // Telephony surface availability is gated PER capability:
    // - Phone surface needs phone.read (or phone.call) AND infra
    // - SMS   surface needs sms.read   (or sms.send)   AND infra
    const isPhoneAvailable = canViewCalls || canMakeCalls;
    const isSmsAvailable = canViewSms || canSendSms;

    return {
      isTelephonyAvailable: isPhoneAvailable || isSmsAvailable,
      isPhoneAvailable,
      isSmsAvailable,
      ...baseFlags,
      isLoading,
      workspaceId,
      phoneNumbers: availableNumbers.map((n) => ({
        id: n.id,
        phoneNumber: n.phoneNumber,
        label: n.label,
      })),
    };
  }, [
    workspaceId,
    integrationStatus,
    phoneNumbers,
    canViewTelephony,
    canViewCalls,
    canMakeCalls,
    canViewSms,
    canSendSms,
    loadingWorkspaces,
    loadingIntegrations,
    loadingNumbers,
  ]);

  return result;
}

export default useTelephonyAvailability;
